import { useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Calendar, Clock, Users, MapPin, CheckCircle2, 
  ArrowRight, ChevronDown, ChevronUp, Play, Target,
  TrendingUp, BarChart3, Zap, Award, BookOpen, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API_BASE } from "../lib/api";

// Assets - Dark logo for light background
const REKISA_LOGO = "https://customer-assets.emergentagent.com/job_growth-workshop/artifacts/5k7dsa9t_Rekisa%20white.png";
const THATO_IMAGE = "https://customer-assets.emergentagent.com/job_growth-workshop/artifacts/q1ocb4xe_Thato%20Image.jpg";

// Partner logos (text placeholders for now)
const PARTNERS = [
  "22onSloane",
  "Wits Business School", 
  "GIBS EDA",
  "YIEDI",
  "Amazon",
  "Takealot"
];

// Workshop modules
const DAY1_MODULES = [
  {
    title: "Growth Marketing Foundations",
    description: "How growth marketing works end to end. The difference between traffic, leads, trust, conversion, and customer marketing.",
    icon: Target
  },
  {
    title: "Build Your Target Market & Lead Engine",
    description: "ICP thinking, ABM lead list logic, audience building for outreach and paid campaigns.",
    icon: Users
  },
  {
    title: "Create Your Content Machine",
    description: "Content that builds trust and demand. How to turn insight into ongoing content assets and warm up cold audiences.",
    icon: BookOpen
  },
  {
    title: "Launch Smarter Outbound",
    description: "AI-personalised outreach logic. Email and LinkedIn as warm-up and conversion channels.",
    icon: Mail
  }
];

const DAY2_MODULES = [
  {
    title: "Set Up Tracking & Know Your Numbers",
    description: "CPL, CAC, unit economics, conversion tracking logic. What to measure and why.",
    icon: BarChart3
  },
  {
    title: "Paid Growth Systems",
    description: "Meta, LinkedIn, Google, retargeting, matched audiences, lookalikes, paid search.",
    icon: TrendingUp
  },
  {
    title: "Conversion Rate Optimisation",
    description: "Landing pages, ad creative alignment, messaging sequences, reducing friction across the funnel.",
    icon: Zap
  },
  {
    title: "Scale What Works",
    description: "How to identify winning channels, improve conversion at each stage, and turn campaigns into a repeatable revenue engine.",
    icon: Award
  }
];

// FAQs
const FAQS = [
  {
    question: "Who is this workshop for?",
    answer: "This workshop is designed for SME founders, ecommerce operators, growth marketers, startup teams, and business owners who want to build better lead generation and conversion systems."
  },
  {
    question: "Is this suitable for ecommerce businesses?",
    answer: "Absolutely. The growth marketing principles covered apply to both B2B and B2C ecommerce businesses. You'll learn how to optimise acquisition, conversion, and customer retention."
  },
  {
    question: "Will I get practical frameworks or only theory?",
    answer: "This is an execution-led workshop. You'll receive practical frameworks, templates, and the Growth Marketing Guides to implement what you learn immediately after the workshop."
  },
  {
    question: "Do I need prior growth marketing experience?",
    answer: "No prior experience required. We start with foundations and build up to advanced strategies. The workshop is designed to meet you where you are."
  },
  {
    question: "Will I get templates or guides?",
    answer: "Yes! You'll receive Rekisa's Growth Marketing Guides - practical execution guides with frameworks and playbooks you can use after the workshop."
  },
  {
    question: "What happens after I pay?",
    answer: "You'll receive a confirmation email with your workshop access details, calendar invite, and joining instructions. Everything you need will be in your inbox within minutes."
  },
  {
    question: "Is the workshop live?",
    answer: "Yes, this is a live online workshop. You'll be able to ask questions and interact directly with Thato Garekoe throughout both sessions."
  },
  {
    question: "What if I can't attend one of the sessions?",
    answer: "While we encourage live attendance for the best experience, registered attendees will receive access to session recordings."
  }
];

// Outcomes
const OUTCOMES = [
  "Define a clearer growth strategy aligned with your business goals",
  "Build a stronger audience and lead-generation foundation",
  "Understand how to use outbound and paid channels together",
  "Improve campaign tracking and conversion visibility",
  "Identify funnel leaks and friction points",
  "Approach growth with a more repeatable system"
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Section wrapper with animation
function AnimatedSection({ children, className = "", id }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.section>
  );
}

// FAQ Item component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="py-5 border-b border-slate-200 last:border-b-0">
      <button
        data-testid={`faq-${question.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-lg font-semibold text-slate-800 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#007AFF] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pt-4 text-slate-600 leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

// Module Card component
function ModuleCard({ module, index, day }) {
  const Icon = module.icon;
  return (
    <motion.div
      variants={fadeInUp}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#007AFF]/10 to-[#10B981]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-[#007AFF]" />
        </div>
        <div>
          <span className="text-xs font-medium text-[#10B981] tracking-wider uppercase">
            Day {day} - Module {index + 1}
          </span>
          <h4 className="text-xl font-semibold text-slate-800 mt-1 mb-2">{module.title}</h4>
          <p className="text-slate-600 leading-relaxed">{module.description}</p>
        </div>
      </div>
    </motion.div>
  );
}

// Lead Form component
function LeadForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    email_opt_in: false,
    whatsapp_opt_in: false
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    try {
      // Get UTM params from URL
      const params = new URLSearchParams(window.location.search);
      
      await axios.post(`${API_BASE}/api/leads`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        email_opt_in: formData.email_opt_in,
        whatsapp_opt_in: formData.whatsapp_opt_in,
        workshop_id: "GMW-2025-04",
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign")
      });
      
      toast.success("Thank you! We'll send you the workshop details.");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        email_opt_in: false,
        whatsapp_opt_in: false
      });
      onSuccess?.();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <input
          data-testid="lead-first-name"
          type="text"
          placeholder="First Name *"
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
        />
        <input
          data-testid="lead-last-name"
          type="text"
          placeholder="Last Name *"
          value={formData.last_name}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
        />
      </div>
      <input
        data-testid="lead-email"
        type="email"
        placeholder="Email Address *"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        className="w-full bg-white border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
      />
      <input
        data-testid="lead-phone"
        type="tel"
        placeholder="Mobile Number *"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        className="w-full bg-white border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
      />
      
      <div className="space-y-3 pt-2">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            data-testid="lead-email-opt-in"
            type="checkbox"
            checked={formData.email_opt_in}
            onChange={(e) => setFormData({ ...formData, email_opt_in: e.target.checked })}
            className="mt-1 w-5 h-5 rounded border-slate-300 text-[#007AFF] focus:ring-[#007AFF] focus:ring-offset-0"
          />
          <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
            I consent to receiving marketing emails about the workshop and related content
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            data-testid="lead-whatsapp-opt-in"
            type="checkbox"
            checked={formData.whatsapp_opt_in}
            onChange={(e) => setFormData({ ...formData, whatsapp_opt_in: e.target.checked })}
            className="mt-1 w-5 h-5 rounded border-slate-300 text-[#007AFF] focus:ring-[#007AFF] focus:ring-offset-0"
          />
          <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
            I consent to receiving WhatsApp messages about the workshop
          </span>
        </label>
      </div>
      
      <Button
        data-testid="lead-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full h-14 mt-4 bg-[#007AFF] text-white font-bold rounded-full hover:bg-[#0066DD] transition-all shadow-lg shadow-[#007AFF]/25"
      >
        {loading ? "Sending..." : "Get Workshop Details"}
      </Button>
      
      <p className="text-xs text-slate-500 text-center">
        By submitting, you agree to our{" "}
        <a href="#" className="text-[#007AFF] hover:underline">Privacy Policy</a>
      </p>
    </form>
  );
}

// Main Landing Page
export default function LandingPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const heroRef = useRef(null);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowStickyCTA(scrollY > 600);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  const handleBuyNow = () => {
    // Scroll to pricing section with offset for fixed nav
    const pricingSection = document.getElementById("pricing");
    if (pricingSection) {
      const offset = 80; // Nav height offset
      const elementPosition = pricingSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };
  
  const handleLeadCapture = () => {
    const leadSection = document.getElementById("lead-form");
    if (leadSection) {
      const offset = 80;
      const elementPosition = leadSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };
  
  const initializePayment = async (formData) => {
    setPaymentLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const response = await axios.post(`${API_BASE}/api/payment/initialize`, {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        callback_url: callbackUrl
      });
      
      // Redirect to Paystack
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Payment initialization failed. Please try again.");
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <a href="/" data-testid="nav-logo" className="flex items-center gap-2">
            <div className="bg-slate-900 px-3 py-1.5 rounded-lg">
              <img src={REKISA_LOGO} alt="Rekisa" className="h-6 md:h-7" />
            </div>
          </a>
          <div className="flex items-center gap-4">
            <Button
              data-testid="nav-details-btn"
              variant="ghost"
              onClick={handleLeadCapture}
              className="hidden md:inline-flex text-slate-700 hover:text-slate-900 hover:bg-slate-100"
            >
              Get Details
            </Button>
            <Button
              data-testid="nav-buy-btn"
              onClick={handleBuyNow}
              className="h-10 px-6 bg-[#007AFF] text-white font-semibold rounded-full hover:bg-[#0066DD] transition-all shadow-lg shadow-[#007AFF]/25"
            >
              Buy Your Seat
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen pt-24 pb-16 md:pt-32 md:pb-24 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left content - 7 cols */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <span className="inline-flex items-center gap-2 text-[#10B981] text-sm font-medium tracking-wider uppercase mb-6 bg-[#10B981]/10 px-4 py-2 rounded-full">
                <Play className="w-4 h-4" />
                Live 2-Day Workshop
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-none tracking-tight mb-6">
                Build a Smarter
                <br />
                <span className="text-gradient-blue">Growth Marketing</span>
                <br />
                Engine in 2 Days
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mb-8">
                Join Thato Garekoe MBA for a live workshop that breaks down how to build lead generation, outbound, paid acquisition, conversion tracking, and scaling systems that actually move revenue.
              </p>
              
              {/* Workshop details */}
              <div className="flex flex-wrap gap-4 md:gap-6 mb-8 text-slate-700">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#007AFF]" />
                  <span>22-23 April 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#007AFF]" />
                  <span>10:00 - 12:00 Daily</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#007AFF]" />
                  <span>Live Online</span>
                </div>
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  data-testid="hero-buy-btn"
                  onClick={handleBuyNow}
                  className="h-14 px-8 bg-[#007AFF] text-white font-bold text-lg rounded-full hover:bg-[#0066DD] hover:scale-105 transition-all shadow-xl shadow-[#007AFF]/30"
                >
                  Buy Your Seat - R1,250
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  data-testid="hero-details-btn"
                  variant="outline"
                  onClick={handleLeadCapture}
                  className="h-14 px-8 border-slate-300 text-slate-700 font-semibold rounded-full hover:bg-white hover:border-slate-400 transition-colors"
                >
                  Get Workshop Details
                </Button>
              </div>
              
              <p className="text-sm text-slate-600">
                <span className="text-[#10B981] font-medium">Limited seats available</span> — Hosted by Thato Garekoe MBA
              </p>
            </motion.div>
            
            {/* Right content - Stats cards */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5"
            >
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "2", label: "Days" },
                  { value: "2h", label: "Per Day" },
                  { value: "Live", label: "Online" },
                  { value: "R1,250", label: "Once-Off" }
                ].map((stat, i) => (
                  <div 
                    key={i}
                    className="bg-white border border-slate-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
                
                {/* Host card - spans 2 cols */}
                <div className="col-span-2 bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-4 shadow-sm">
                  <img 
                    src={THATO_IMAGE} 
                    alt="Thato Garekoe"
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#007AFF]"
                  />
                  <div>
                    <div className="text-slate-900 font-semibold">Thato Garekoe MBA</div>
                    <div className="text-sm text-slate-500">Founder & CEO, Rekisa</div>
                  </div>
                </div>
                
                {/* Bonus card */}
                <div className="col-span-2 bg-gradient-to-r from-[#007AFF] to-[#10B981] rounded-xl p-6 text-center text-white">
                  <div className="font-medium mb-1 opacity-90">Includes Bonus</div>
                  <div className="font-bold text-lg">Growth Marketing Guides</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <AnimatedSection className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-slate-600 mb-8">
            We've run this programme for over <span className="text-slate-900 font-semibold">3,000+ SMEs</span> on behalf of leading institutions
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {PARTNERS.map((partner, i) => (
              <div 
                key={i}
                className="text-xl md:text-2xl font-semibold text-slate-400 hover:text-slate-700 transition-all cursor-default"
              >
                {partner}
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Problem Section */}
      <AnimatedSection className="py-24 md:py-32 section-alt">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
            The Growth Gap
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
            Active in Marketing.
            <br />
            <span className="text-slate-500">But Missing a System.</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
            Most businesses are posting, spending, boosting, and trying channels — without clear tracking, conversion logic, or repeatable scale. There's a gap between <span className="text-slate-900 font-medium">random marketing activity</span> and a <span className="text-[#10B981] font-medium">real growth marketing system</span>.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-left shadow-sm">
              <h4 className="text-xl font-semibold text-slate-400 mb-4 line-through">Random Activity</h4>
              <ul className="space-y-3 text-slate-500">
                <li>• Posting without strategy</li>
                <li>• Boosting posts randomly</li>
                <li>• No conversion tracking</li>
                <li>• Unclear ROI on spend</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#007AFF]/5 to-[#10B981]/5 border-2 border-[#007AFF]/20 rounded-xl p-8 text-left">
              <h4 className="text-xl font-semibold text-[#10B981] mb-4">Growth System</h4>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  Clear audience targeting
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  Multi-channel coordination
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  CPL & CAC visibility
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  Repeatable revenue engine
                </li>
              </ul>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Curriculum Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Workshop Curriculum
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              What You'll Learn
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              8 practical modules across 2 days, built around Rekisa's Growth Marketing System
            </p>
          </div>
          
          {/* Day 1 */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#007AFF]/30 to-transparent"></div>
              <h3 className="text-xl font-semibold text-slate-800 px-4 bg-white">
                Day 1 — 22 April | 10:00 - 12:00
              </h3>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#007AFF]/30 to-transparent"></div>
            </div>
            <motion.div 
              variants={staggerChildren}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-6"
            >
              {DAY1_MODULES.map((module, i) => (
                <ModuleCard key={i} module={module} index={i} day={1} />
              ))}
            </motion.div>
          </div>
          
          {/* Day 2 */}
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#10B981]/30 to-transparent"></div>
              <h3 className="text-xl font-semibold text-slate-800 px-4 bg-white">
                Day 2 — 23 April | 10:00 - 12:00
              </h3>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#10B981]/30 to-transparent"></div>
            </div>
            <motion.div 
              variants={staggerChildren}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 gap-6"
            >
              {DAY2_MODULES.map((module, i) => (
                <ModuleCard key={i} module={module} index={i} day={2} />
              ))}
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Bonus Section */}
      <AnimatedSection className="py-24 md:py-32 section-alt">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white border-2 border-[#007AFF]/20 rounded-2xl p-8 md:p-12 text-center shadow-xl shadow-[#007AFF]/5">
            <span className="inline-block bg-[#10B981] text-white text-sm font-bold px-4 py-1 rounded-full mb-6">
              BONUS INCLUDED
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Growth Marketing Guides
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              You won't leave with theory only. You'll also get Rekisa's Growth Marketing Guides — practical execution guides with detailed frameworks and playbooks to help you implement what you've learned immediately after the workshop.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: BookOpen, text: "Implementation Frameworks" },
                { icon: Target, text: "Campaign Templates" },
                { icon: BarChart3, text: "Tracking Playbooks" }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-[#007AFF]" />
                  </div>
                  <span className="text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Outcomes Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Workshop Outcomes
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              By the End, You'll Be Able To...
            </h2>
          </div>
          
          <motion.div 
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {OUTCOMES.map((outcome, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5 hover:bg-white hover:shadow-md transition-all"
              >
                <CheckCircle2 className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-lg">{outcome}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Host Section */}
      <AnimatedSection className="py-24 md:py-32 section-alt">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/20 to-[#10B981]/20 rounded-2xl blur-3xl opacity-50"></div>
              <img 
                src={THATO_IMAGE} 
                alt="Thato Garekoe MBA"
                className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
                Your Host
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Thato Garekoe MBA
              </h2>
              <p className="text-[#10B981] font-medium mb-6">Founder & CEO, Rekisa</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Thato is a practitioner, operator, and strategic growth partner with deep experience helping SMEs and commerce businesses improve how they acquire, convert, and retain customers.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Having delivered growth marketing programmes for over 3,000 SMEs on behalf of leading institutions, Thato brings real-world frameworks that have been tested and proven across diverse business contexts.
              </p>
              <a 
                href="https://linkedin.com/in/thatogarekoe" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#007AFF] hover:text-[#0066DD] font-medium transition-colors"
              >
                Connect on LinkedIn
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Schedule Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Workshop Schedule
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              2 Days. 4 Hours. Full System.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Day 1 */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#007AFF] flex items-center justify-center">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <div className="text-slate-900 font-semibold">Day 1</div>
                  <div className="text-slate-500 text-sm">22 April | 10:00 - 12:00</div>
                </div>
              </div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#007AFF]"></div>
                  Growth Marketing Foundations
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#007AFF]"></div>
                  Audience & Lead Engine
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#007AFF]"></div>
                  Content Machine
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#007AFF]"></div>
                  Outbound Systems
                </li>
              </ul>
            </div>
            
            {/* Day 2 */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#10B981] flex items-center justify-center">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <div className="text-slate-900 font-semibold">Day 2</div>
                  <div className="text-slate-500 text-sm">23 April | 10:00 - 12:00</div>
                </div>
              </div>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  Tracking & Unit Economics
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  Paid Growth Systems
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  Conversion Optimisation
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  Scaling What Works
                </li>
              </ul>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Pricing Section */}
      <AnimatedSection id="pricing" className="py-24 md:py-32 section-alt">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Investment
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              Secure Your Seat
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-2xl shadow-slate-200/50 overflow-hidden">
            <div className="bg-gradient-to-r from-[#007AFF] to-[#10B981] p-1">
              <div className="bg-white p-8 md:p-12 rounded-t-xl">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Price info */}
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Growth Marketing Workshop
                    </h3>
                    <div className="flex items-baseline gap-2 mb-6">
                      <span className="text-5xl md:text-6xl font-bold text-slate-900">R1,250</span>
                      <span className="text-slate-500">once-off</span>
                    </div>
                    
                    <ul className="space-y-4 mb-8">
                      {[
                        "2 live workshop sessions",
                        "Access to Growth Marketing Guides",
                        "Live training with Thato Garekoe",
                        "Session recordings access",
                        "Confirmation email after purchase"
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-700">
                          <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Payment form */}
                  <div>
                    <PaymentForm onSubmit={initializePayment} loading={paymentLoading} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Lead Capture Section */}
      <AnimatedSection id="lead-form" className="py-24 md:py-32 bg-white">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Not Ready to Buy?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Get Workshop Details
            </h2>
            <p className="text-lg text-slate-600">
              We'll send you all the information you need to make a decision.
            </p>
          </div>
          
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <LeadForm />
          </div>
        </div>
      </AnimatedSection>

      {/* FAQ Section */}
      <AnimatedSection className="py-24 md:py-32 section-alt">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-[#007AFF] text-sm font-medium tracking-wider uppercase mb-4 block">
              Questions?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
              Frequently Asked
            </h2>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Final CTA */}
      <AnimatedSection className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Stop Guessing.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#007AFF] to-[#10B981]">Build a Clearer Growth System.</span>
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Join the Growth Marketing Workshop on 22-23 April and start building a marketing system that actually moves revenue.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="final-buy-btn"
              onClick={handleBuyNow}
              className="h-16 px-10 bg-[#007AFF] text-white font-bold text-lg rounded-full hover:bg-[#0066DD] hover:scale-105 transition-all shadow-xl shadow-[#007AFF]/30"
            >
              Buy Your Seat - R1,250
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              data-testid="final-details-btn"
              variant="outline"
              onClick={handleLeadCapture}
              className="h-16 px-10 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
            >
              Get Workshop Details
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="bg-white/10 px-3 py-1.5 rounded-lg">
              <img src={REKISA_LOGO} alt="Rekisa" className="h-6" />
            </div>
            <p className="text-slate-400 text-sm">
              © 2025 Rekisa. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky CTA */}
      <div className={`sticky-cta ${showStickyCTA ? 'visible' : ''}`}>
        <Button
          data-testid="sticky-buy-btn"
          onClick={handleBuyNow}
          className="h-14 px-8 bg-[#007AFF] text-white font-bold rounded-full shadow-2xl hover:bg-[#0066DD] hover:scale-105 transition-all"
        >
          Buy Your Seat - R1,250
        </Button>
      </div>
    </div>
  );
}

// Payment Form Component
function PaymentForm({ onSubmit, loading }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: ""
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    onSubmit(formData);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <input
          data-testid="payment-first-name"
          type="text"
          placeholder="First Name *"
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
        />
        <input
          data-testid="payment-last-name"
          type="text"
          placeholder="Last Name *"
          value={formData.last_name}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
        />
      </div>
      <input
        data-testid="payment-email"
        type="email"
        placeholder="Email Address *"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
      />
      <input
        data-testid="payment-phone"
        type="tel"
        placeholder="Mobile Number *"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-800 placeholder:text-slate-400 focus:border-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all"
      />
      
      <Button
        data-testid="payment-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full h-14 bg-[#007AFF] text-white font-bold rounded-full hover:bg-[#0066DD] transition-all shadow-lg shadow-[#007AFF]/25"
      >
        {loading ? "Processing..." : "Proceed to Payment"}
        {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
      </Button>
      
      <p className="text-xs text-slate-500 text-center">
        Secure payment powered by Paystack
      </p>
    </form>
  );
}
